import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    MenuItem,
    Paper,
    Skeleton,
    Slide,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import {InputField, SnackbarField} from '@/components/common';
import CompanyFSCategoryOpera from '@/pages/components/manage/category/co-fs-cate/opera/CompanyFSCategoryOpera';
import CompanyFsEditorPreview from '@/pages/components/manage/category/co-fs-cate/opera/CompanyFsEditorPreview';
import {CompanyIndustryService} from '@/services/accountService';
import type {CompanyIndustry} from '@/services/accountService/companyIndustry';
import * as CompanyService from '@/services/accountService/companyService';
import * as FsCategoryService from '@/services/accountService/fsCategoryService';
import {usePageCrud} from '@/hooks/usePageCrud';

type DetailView = 'operation' | 'preview';

const AuditCompanyFSCategoryList: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pageNum, setPageNum] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(25);
    const [total, setTotal] = useState<number>(0);
    const [filterCompanyName, setFilterCompanyName] = useState('');
    const [filterIndustry, setFilterIndustry] = useState<number | ''>('');
    const [industries, setIndustries] = useState<CompanyIndustry[]>([]);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message?: string;
        severity?: 'success' | 'error'
    }>({open: false});
    const [showDetail, setShowDetail] = useState(false);
    const [detailView, setDetailView] = useState<DetailView | null>(null);
    const [selectedCompany, setSelectedCompany] = useState<{ id: number; name?: string } | null>(null);
    const [companies, setCompanies] = useState<CompanyService.CompanyInfo[]>([]);
    const [operaBsTree, setOperaBsTree] = useState<FsCategoryService.FsCategory[]>([]);
    const [operaPlTree, setOperaPlTree] = useState<FsCategoryService.FsCategory[]>([]);
    const {canRead, canUpdate} = usePageCrud({
        read: ['co_fs_cate_list_by_company', 'co_fs_cate_get'],
        create: 'audit:co_fs_cate:create',
        update: 'audit:co_fs_cate:update',
        remove: 'audit:co_fs_cate:delete',
    });

    // Load industry options from /companyIndustry/selectList (same as CompanyList)
    useEffect(() => {
        (async () => {
            try {
                const res = await CompanyIndustryService.companySelectList();
                let options: CompanyIndustry[] = [];
                if (Array.isArray(res)) {
                    options = res.map((it: {
                        industryId?: number;
                        id?: number;
                        value?: number;
                        industryName?: string;
                        name?: string;
                        label?: string;
                    }) => ({
                        industryId: it?.industryId ?? it?.id ?? it?.value,
                        industryName: it?.industryName ?? it?.name ?? it?.label,
                    })).filter((o) => o.industryId != null);
                } else if (res && typeof res === 'object') {
                    // Backend returns Map<Long, String>: { industryId: industryName }
                    options = Object.entries(res).map(([k, v]) => ({
                        industryId: Number(k),
                        industryName: String(v),
                    }));
                }
                setIndustries(options);
            } catch {
                setIndustries([]);
            }
        })();
    }, []);

    useEffect(() => {
        if (filterIndustry === '') return;
        const exists = industries.some(i => i.industryId === filterIndustry);
        if (!exists) setFilterIndustry('');
    }, [industries, filterIndustry]);

    const loadCompanies = useCallback(async () => {
        setLoading(true);
        try {
            const effectiveIndustry: number | undefined = filterIndustry !== '' ? filterIndustry : undefined;
            const resp = await CompanyService.pageCompanies({
                pageNumber: pageNum,
                pageSize,
                pageType: 1,
                companyName: filterCompanyName || undefined,
                industry: effectiveIndustry,
            });
            const arr: CompanyService.CompanyInfo[] = resp?.list || [];
            setCompanies(arr);
            setTotal(Number(resp?.totalRow || 0));
            setError(null);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Load failed');
        } finally {
            setLoading(false);
        }
    }, [pageNum, pageSize, filterCompanyName, filterIndustry]);

    useEffect(() => {
        const handler = setTimeout(loadCompanies, 400);
        return () => clearTimeout(handler);
    }, [loadCompanies]);

    const tableRows = useMemo(() => {
        return (companies || []).map(c => ({
            id: Number(c.companyId),
            companyName: c.companyName,
            companyChineseName: c.companyChineseName,
            industry: c.industryName || c.industry,
        }));
    }, [companies]);

    // Match AuditAdjustmentList / GlList: exit Slide first, then clear selection
    const handleBack = () => {
        setShowDetail(false);
        setTimeout(() => {
            setSelectedCompany(null);
            setDetailView(null);
        }, 400);
    };

    const openOperation = async (row: { id: number; companyName?: string; companyChineseName?: string; }) => {
        const target = {
            id: Number(row.id),
            name: row.companyName || row.companyChineseName,
        };
        setSelectedCompany(target);
        try {
            const [bs, pl] = await Promise.all([
                FsCategoryService.queryRemoveRootTreeByStatement('BS'),
                FsCategoryService.queryRemoveRootTreeByStatement('PL'),
            ]);
            const toArray = (x: unknown) =>
                Array.isArray(x) ? x : ((x as { items?: unknown; list?: unknown })?.items
                    ?? (x as { list?: unknown })?.list
                    ?? []);
            setOperaBsTree(toArray(bs) as FsCategoryService.FsCategory[]);
            setOperaPlTree(toArray(pl) as FsCategoryService.FsCategory[]);
            setDetailView('operation');
            setShowDetail(true);
        } catch {
            setSelectedCompany(null);
        }
    };

    const openPreview = (row: { id: number; companyName?: string; companyChineseName?: string; }) => {
        setSelectedCompany({
            id: Number(row.id),
            name: row.companyName || row.companyChineseName,
        });
        setDetailView('preview');
        setShowDetail(true);
    };

    const detailTitle = detailView === 'operation'
        ? `Operation Company FS Category - ${selectedCompany?.name ?? selectedCompany?.id}`
        : `Preview Company FS Category - ${selectedCompany?.name ?? selectedCompany?.id}`;

    return (
        <Box sx={{p: '20px', height: '100%', boxSizing: 'border-box'}}>
            <Box sx={{position: 'relative', height: 'calc(100vh - 120px)', minHeight: '70vh'}}>
                <Slide direction="left" in={!showDetail} mountOnEnter unmountOnExit>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        pr: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                               alignItems={{xs: 'stretch', sm: 'center'}} gap={1} mb={2} flexShrink={0}>
                            <Typography variant="h6" fontWeight={700}>Audit Company FS Category</Typography>
                            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}
                                   alignItems={{xs: 'stretch', sm: 'center'}}>
                                <InputField
                                    size="small"
                                    label="Company Name"
                                    value={filterCompanyName}
                                    onChange={e => {
                                        setPageNum(1);
                                        setFilterCompanyName(e.target.value);
                                    }}
                                />
                                <InputField
                                    label="Industry"
                                    select
                                    fullWidth
                                    placeholder=""
                                    value={filterIndustry !== '' && industries.some(i => i.industryId === filterIndustry)
                                        ? String(filterIndustry)
                                        : ''}
                                    onChange={(e) => {
                                        const v = e.target.value as string;
                                        setPageNum(1);
                                        setFilterIndustry(v === '' ? '' : Number(v));
                                    }}
                                    sx={{minWidth: 180}}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {industries.map((o, idx) => (
                                        <MenuItem
                                            key={o.industryId ?? `ind-${idx}`}
                                            value={o.industryId != null ? String(o.industryId) : ''}
                                        >
                                            {o.industryName || '-'}
                                        </MenuItem>
                                    ))}
                                </InputField>
                            </Stack>
                        </Stack>
                        {error && (
                            <Box sx={{color: 'error.main', px: 1, py: 0.5, fontSize: 12, flexShrink: 0}}>{error}</Box>
                        )}
                        <TableContainer component={Paper} sx={{flex: 1, minHeight: 0, overflow: 'auto'}}>
                            <Table
                                size="small"
                                stickyHeader
                                sx={(theme) => ({
                                    '& .MuiTableCell-stickyHeader': {
                                        bgcolor: 'background.paper',
                                        zIndex: 3,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    },
                                })}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 700}}>Company Name</TableCell>
                                        <TableCell sx={{fontWeight: 700}}>Company Chinese Name</TableCell>
                                        <TableCell sx={{fontWeight: 700}} align="center">Industry</TableCell>
                                        <TableCell sx={{fontWeight: 700}} align="center">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading && [...Array(pageSize).keys()].map((i) => (
                                        <TableRow key={`sk-${i}`}>
                                            <TableCell><Skeleton variant="text"/></TableCell>
                                            <TableCell><Skeleton variant="text"/></TableCell>
                                            <TableCell><Skeleton variant="text"/></TableCell>
                                            <TableCell><Skeleton variant="rounded" height={24}/></TableCell>
                                        </TableRow>
                                    ))}
                                    {!loading && tableRows.length === 0 && (
                                        <TableRow><TableCell colSpan={4} align="center">No data</TableCell></TableRow>
                                    )}
                                    {!loading && tableRows.map((r) => (
                                        <TableRow key={String(r.id)} hover>
                                            <TableCell>{r.companyName}</TableCell>
                                            <TableCell>{r.companyChineseName}</TableCell>
                                            <TableCell align="center">{r.industry ?? '-'}</TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                    {canUpdate && (
                                                    <Button size="small" variant="outlined"
                                                            sx={{textTransform: 'none', fontWeight: 600}}
                                                            onClick={() => openOperation(r)}>
                                                        Operation
                                                    </Button>
                                                    )}
                                                    {canRead && (
                                                    <Button size="small" variant="outlined"
                                                            sx={{textTransform: 'none', fontWeight: 600}}
                                                            onClick={() => openPreview(r)}>
                                                        Preview
                                                    </Button>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={Math.max(0, Math.min(Math.ceil(total / pageSize) - 1, pageNum - 1))}
                            onPageChange={(_, newPage) => setPageNum(newPage + 1)}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={(e) => {
                                const next = parseInt(e.target.value, 10) || 25;
                                setPageSize(next);
                                setPageNum(1);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            sx={{mt: 1, flexShrink: 0}}
                        />
                    </Box>
                </Slide>

                <Slide direction="right" in={showDetail} mountOnEnter unmountOnExit>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        pl: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        {selectedCompany ? (
                            <>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexShrink={0}>
                                    <Typography variant="h6" fontWeight={700}>{detailTitle}</Typography>
                                    <Button size="small" onClick={handleBack} sx={{textTransform: 'none', fontWeight: 600}}>
                                        Back
                                    </Button>
                                </Stack>
                                <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
                                    {detailView === 'operation' && (
                                        <CompanyFSCategoryOpera
                                            bsTree={operaBsTree}
                                            plTree={operaPlTree}
                                            companyId={selectedCompany.id}
                                            companyName={selectedCompany.name}
                                        />
                                    )}
                                    {detailView === 'preview' && (
                                        <CompanyFsEditorPreview
                                            companyId={selectedCompany.id}
                                            companyName={selectedCompany.name}
                                        />
                                    )}
                                </Box>
                            </>
                        ) : (
                            <Box sx={{p: 2, color: 'text.secondary'}}>Select a company.</Box>
                        )}
                    </Box>
                </Slide>

                <SnackbarField open={!!snackbar.open} message={snackbar.message || ''} severity={snackbar.severity}
                               onClose={() => setSnackbar(s => ({...s, open: false}))}/>
            </Box>
        </Box>
    );
};

export default AuditCompanyFSCategoryList;
